import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle, Eye, EyeOff, Lock } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import EntityConstants from "../../configs/EntityConstants";
import { POST } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import { BaseLayoutContext } from "../../store/BaseLayoutContext";
import { useTranslation } from "react-i18next";

const ForgotPasswordAccount = () => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const baseLayoutContext = useContext(BaseLayoutContext);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [validPassword, setValidPassword] = useState(false);
  const [hasServerError, setHasServerError] = useState(false);
  const [errorHeading, setErrorHeading] = useState("");
  const [dataMessage, setDataMessage] = useState("");
  const [autoOpenTriggered, setAutoOpenTriggered] = useState(false);
  const [modalOpenedOnce, setModalOpenedOnce] = useState(false);
  const [showManualSignIn, setShowManualSignIn] = useState(false);
  const successTimeoutRef = useRef(null);

  const { uuid, token } = useParams();

  const validationSchema = Yup.object({
    password: Yup.string()
      .required(`${t("homeAuthPages:password_required")}`)
      .matches(/^(?=.*[A-Z])/, `${t("homeAuthPages:password_uppercase")}`)
      .matches(/^(?=.*[a-z])/, `${t("homeAuthPages:password_lowercase")}`)
      .matches(/^(?=.*\d)/, `${t("homeAuthPages:must_contain_number")}`)
      .matches(
        /^(?=.*[~`!@#$%^&*()_\-+={[}\]|:;"'<,>.?/])/,
        `${t("homeAuthPages:must_contain_special_char")}`,
      )
      .min(8, `${t("homeAuthPages:password_min_length")}`),
    confirm_password: Yup.string()
      .required(`${t("homeAuthPages:confirm_password_required")}`)
      .oneOf([Yup.ref("password")], `${t("homeAuthPages:passwords_mismatch")}`),
  });

  const openSignInModal = () => {
    authContext.isSignUpHandler(false);
    baseLayoutContext.setTriggerLoginModuleToggleValue(true);
  };

  const updatePassword = async (uuid, token, password) => {
    try {
      setHasServerError(false);
      setInvalidPassword(false);
      const response = await POST(EntityConstants.POST_UPDATE_PASSWORD, {
        uuid,
        token,
        password,
      });
      const data = await response.json();
      if (response.ok) {
        const successMessage =
          data?.message || t("resetPassword:success_message");
        console.log(successMessage);
        toast.success(successMessage);

        setInvalidPassword(false);
        setHasServerError(false);
        setValidPassword(true);
        setErrorHeading("");
        setDataMessage(successMessage);
        setAutoOpenTriggered(false);
        setModalOpenedOnce(false);
        setShowManualSignIn(false);
        successTimeoutRef.current = setTimeout(() => {
          openSignInModal();
          setAutoOpenTriggered(true);
        }, 1500);
      }
    } catch (error) {
      console.error("Failed to update password", error);
      let backendErrorHeading = "";
      let backendErrorMessage = "";

      if (error?.response) {
        try {
          const errorData = await error.response.json();
          backendErrorHeading = errorData?.error || "";
          backendErrorMessage = errorData?.message || "";
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }
      }

      const fallbackMessage = t("homeAuthPages:password_update_error");
      const errorHeadingValue = backendErrorHeading || "error";
      const errorMessageValue =
        backendErrorMessage || backendErrorHeading || fallbackMessage;

      toast.error(errorMessageValue);
      setHasServerError(true);
      setInvalidPassword(true);
      setValidPassword(false);
      setErrorHeading(errorHeadingValue);
      setDataMessage(errorMessageValue);
    }
  };

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!validPassword || !autoOpenTriggered) {
      return;
    }

    if (baseLayoutContext.triggerLoginModuleToggleValue) {
      if (!modalOpenedOnce) {
        setModalOpenedOnce(true);
      }
      return;
    }

    if (modalOpenedOnce && !authContext.isLoggedIn) {
      setShowManualSignIn(true);
    }
  }, [
    validPassword,
    autoOpenTriggered,
    modalOpenedOnce,
    baseLayoutContext.triggerLoginModuleToggleValue,
    authContext.isLoggedIn,
  ]);

  return (
    <div className="w-full min-h-screen bg-cfSecondary flex items-center justify-center px-4 py-8">
      <div
        className={`w-full ${
          hasServerError || validPassword ? "max-w-2xl" : "max-w-lg"
        }`}
      >
        {!hasServerError && !validPassword && (
          <div className="flex flex-col items-center text-center mb-7">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-cfDisabledLight rounded-full blur-xl"></div>
                <div className="relative p-[1.5rem] rounded-full bg-cfDisabledLight border-[0.1rem] border-cfDisabled">
                  <Lock className="w-7 h-7 text-cfPrimary" />
                </div>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-3 text-balance">
              {t("homeAuthPages:change_password")}
            </h1>
            <p className="text-cfSignInSignUpIntroMessageText text-base">
              {t("homeAuthPages:enter_new_password")}
            </p>
          </div>
        )}

        <div
          className={`bg-card border-1 rounded-xl shadow-lg backdrop-blur-sm overflow-hidden ${
            hasServerError
              ? "bg-white border-[#d0d5dd] rounded-[2rem] shadow-[0_8px_24px_rgba(16,24,40,0.08)]"
              : validPassword
                ? "bg-white border-[#d0d5dd] rounded-[2rem] shadow-[0_8px_24px_rgba(16,24,40,0.08)]"
                : "border-[#b1b7f2]"
          }`}
        >
          {!validPassword && !hasServerError ? (
            <Formik
              initialValues={{ password: "", confirm_password: "" }}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                updatePassword(uuid, token, values.password);
              }}
            >
              {({ values, isSubmitting }) => (
                <Form>
                  <div className="flex flex-col">
                    <div className="p-6 md:p-8 md:border-r md:border-border">
                      <div className="space-y-3">
                        <label
                          htmlFor="password"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                        >
                          {t("homeAuthPages:new_password")}
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                          <Field
                            type={showNewPassword ? "text" : "password"}
                            name="password"
                            id="reset-password-new-input"
                            placeholder={t("homeAuthPages:new_password")}
                            className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full bg-transparent"
                          />
                          <button
                            id="reset-password-show-new-button"
                            type="button"
                            className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                            aria-label={
                              showNewPassword
                                ? t("resetPassword:hide_password")
                                : t("resetPassword:show_password")
                            }
                            onClick={() => setShowNewPassword((prev) => !prev)}
                          >
                            {showNewPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        <ErrorMessage
                          name="password"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      <div className="space-y-3 mt-4">
                        <label
                          htmlFor="confirm_password"
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                        >
                          {t("homeAuthPages:confirm_password")}
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                          <Field
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirm_password"
                            id="reset-password-confirm-input"
                            placeholder={t("homeAuthPages:confirm_password")}
                            className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full bg-transparent"
                          />
                          <button
                            id="reset-password-show-confirm-button"
                            type="button"
                            className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                            aria-label={
                              showConfirmPassword
                                ? t("resetPassword:hide_password")
                                : t("resetPassword:show_password")
                            }
                            onClick={() =>
                              setShowConfirmPassword((prev) => !prev)
                            }
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        <ErrorMessage
                          name="confirm_password"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {invalidPassword && dataMessage && !hasServerError && (
                        <p id="reset-password-error-description" className="text-red-500 text-sm mt-4">
                          {dataMessage}
                        </p>
                      )}

                      <div className="grid grid-cols-1 gap-4 mt-6 w-full">
                        <button
                          id="reset-password-submit-button"
                          type="submit"
                          disabled={
                            isSubmitting ||
                            !values.password ||
                            !values.confirm_password
                          }
                          className="rounded-[8px] text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark h-[3rem] px-2 text-cfSecondary focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {t("homeAuthPages:change_password")}
                        </button>
                      </div>
                    </div>

                    <div className="p-6 md:p-8 bg-cfSearchContainer flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        <p className="text-md font-bold text-foreground">
                          {t("resetPassword:requirements_title")}
                        </p>
                      </div>
                      <ul className="list-disc pl-2 text-sm text-cfSignInSignUpInputPlaceholderText space-y-2 md:space-y-3">
                        <li>{t("resetPassword:requirement_min_length")}</li>
                        <li>
                          {t("resetPassword:requirement_mixed_characters")}
                        </li>
                        <li>
                          {t("resetPassword:requirement_allowed_characters")}
                        </li>
                        <li>{t("resetPassword:requirement_no_repeat")}</li>
                      </ul>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          ) : validPassword ? (
            <div className="p-8 md:p-10 text-center bg-white">
              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full bg-[#E8F5EC] flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-[#12B76A]" />
                </div>
              </div>
              <h2 className="mt-6 text-2xl font-semibold text-[#101828]">
                {t("homeAuthPages:password_updated_successfully")}
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-lg leading-[1.4] text-[#667085]">
                {dataMessage}
              </p>
              {showManualSignIn && (
                <div className="mt-8 flex justify-center">
                  <button
                    id="reset-password-success-sign-in-button"
                    type="button"
                    className="rounded-[8px] focus:outline-none text-md font-semibold bg-[#2648B8] hover:bg-[#1f3d9a] px-4 py-2.5 text-white"
                    onClick={openSignInModal}
                  >
                    {t("homeAuthPages:sign_in")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 md:p-10 text-center bg-white">
              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full bg-[#fee4e2] flex items-center justify-center">
                  <AlertTriangle className="w-10 h-10 text-[#D92D20]" />
                </div>
              </div>
              <h2 className="mt-6 text-2xl font-semibold text-[#101828] text-ellipsis truncate">
                {errorHeading}
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-lg leading-[1.4] text-[#667085]">
                {dataMessage}
              </p>
              <div className="mt-8 flex justify-center cursor-pointer">
                <button
                  id="reset-password-error-sign-in-button"
                  type="button"
                  className="rounded-[8px] focus:outline-none text-md font-semibold bg-[#2648B8] hover:bg-[#1f3d9a] px-4 py-2.5 text-white "
                  onClick={openSignInModal}
                >
                  {t("homeAuthPages:sign_in")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordAccount;
